// permissions.config.ts

import { RolesEnum } from 'src/app/features/tests/features/constants';
import { AbilityTuple, Action, Subject } from './ability.type';

/**
 * Creating a SignedReport — the "Approve and Sign" action — is granted to the
 * two physician roles plus Super Admin and Tech Admin, who sign on a lab's
 * behalf when support needs to. Signing records the signer as the test's
 * interpreting physician, so the roles that only prepare a report (scorers,
 * technicians, lab managers, admin) keep their editing rights but cannot
 * approve it. They can still open an unsigned PDF preview, which is not gated
 * on any permission.
 */
const BASE_ROLE_PERMISSIONS: Record<
  Exclude<RolesEnum, RolesEnum.PhysicianNoSign>,
  AbilityTuple[]
> = {
  [RolesEnum.SuperAdmin]: [
    [Action.View, Subject.TechNote],
    [Action.Edit, Subject.TechNote],
    [Action.View, Subject.ScorerNote],
    [Action.Edit, Subject.ScorerNote],
    [Action.Generate, Subject.Report],

    [Action.View, Subject.DashboardPage],
    [Action.View, Subject.TestRoomPage],
    [Action.View, Subject.PatientsPage],
    [Action.View, Subject.TestsPage],

    [Action.View, Subject.TestsLogs],
    [Action.View, Subject.TestsReport],
    [Action.View, Subject.LiveTestsPage],

    [Action.View, Subject.DevicesPage],
    [Action.View, Subject.SettingsPage],
    [Action.View, Subject.ReportsPage],
    [Action.View, Subject.WorkingAreaPage],
    [Action.View, Subject.SystemLogsPage],

    [Action.View, Subject.Patient],
    [Action.Edit, Subject.Patient],
    [Action.Create, Subject.Patient],
    [Action.Delete, Subject.Patient],

    [Action.View, Subject.Order],
    [Action.Edit, Subject.Order],
    [Action.Create, Subject.Order],
    [Action.Delete, Subject.Order],

    [Action.View, Subject.TestRecord],
    [Action.Edit, Subject.TestRecord],
    [Action.Create, Subject.TestRecord],
    [Action.Delete, Subject.TestRecord],
    [Action.Edit, Subject.TestBackupFile],

    [Action.View, Subject.Algo],
    [Action.Edit, Subject.Algo],
    [Action.Create, Subject.Algo],
    [Action.Delete, Subject.Algo],
    [Action.Create, Subject.EdfExport],

    [Action.View, Subject.Device],
    [Action.Edit, Subject.Device],
    [Action.Create, Subject.Device],
    [Action.Delete, Subject.Device],

    // Shipping legs: carrier / tracking / est. delivery / direction, and their
    // history.
    [Action.View, Subject.DeviceShipment],
    [Action.Edit, Subject.DeviceShipment],
    [Action.Create, Subject.DeviceShipment],
    // Naming a device and moving it between departments — inventory upkeep,
    // separate from editing what the device is.
    [Action.Edit, Subject.DeviceInventory],
    // Deactivating a device (taking it out of the active inventory) and
    // reactivating it. Same four roles as the inventory upkeep above.
    [Action.Edit, Subject.DeviceActivation],

    [Action.View, Subject.DeviceStatus],
    [Action.Edit, Subject.DeviceStatus],

    [Action.View, Subject.Department],
    [Action.Edit, Subject.Department],
    [Action.Create, Subject.Department],
    [Action.Delete, Subject.Department],

    [Action.View, Subject.Annotation],
    [Action.Edit, Subject.Annotation],
    [Action.Create, Subject.Annotation],
    [Action.Delete, Subject.Annotation],

    [Action.View, Subject.Analysis],
    [Action.Edit, Subject.Analysis],
    [Action.Create, Subject.Analysis],
    [Action.Delete, Subject.Analysis],

    [Action.View, Subject.Report],
    [Action.Edit, Subject.Report],
    [Action.Create, Subject.Report],
    [Action.Delete, Subject.Report],

    [Action.Create, Subject.SignedReport],

    [Action.Edit, Subject.CustomText],
    [Action.Create, Subject.CustomText],
    [Action.Delete, Subject.CustomText],

    [Action.View, Subject.ReferringDoctors],
    [Action.Edit, Subject.ReferringDoctors],
    [Action.Create, Subject.ReferringDoctors],
    [Action.Delete, Subject.ReferringDoctors],

    [Action.View, Subject.TestRoom],
    [Action.Edit, Subject.TestRoom],
    [Action.Create, Subject.TestRoom],
    [Action.Delete, Subject.TestRoom],

    [Action.View, Subject.User],
    [Action.Edit, Subject.User],
    [Action.Create, Subject.User],
    [Action.Delete, Subject.User],

    [Action.View, Subject.PSGHSTestsPage],
    [Action.View, Subject.RAWTestsPage],
  ],

  [RolesEnum.Admin]: [
    [Action.View, Subject.TechNote],
    [Action.Edit, Subject.TechNote],
    [Action.View, Subject.ScorerNote],
    [Action.Edit, Subject.ScorerNote],
    [Action.View, Subject.DashboardPage],
    [Action.View, Subject.PatientsPage],
    [Action.View, Subject.TestsPage],

    [Action.View, Subject.TestsLogs],
    [Action.View, Subject.TestsReport],
    [Action.View, Subject.LiveTestsPage],

    [Action.View, Subject.DevicesPage],
    [Action.View, Subject.TestRoomPage],
    [Action.View, Subject.SettingsPage],
    [Action.View, Subject.ReportsPage],
    [Action.View, Subject.WorkingAreaPage],
    [Action.View, Subject.SystemLogsPage],

    [Action.View, Subject.Patient],
    [Action.Edit, Subject.Patient],
    [Action.Create, Subject.Patient],
    [Action.Delete, Subject.Patient],

    [Action.View, Subject.Order],
    [Action.Edit, Subject.Order],
    [Action.Create, Subject.Order],
    [Action.Delete, Subject.Order],

    [Action.View, Subject.TestRecord],
    [Action.Edit, Subject.TestRecord],
    [Action.Create, Subject.TestRecord],
    [Action.Delete, Subject.TestRecord],
    //admin does not get TestBackupFile: "Upload Backup File" is internal tooling

    [Action.View, Subject.Algo],
    [Action.Edit, Subject.Algo],
    [Action.Create, Subject.Algo],
    [Action.Delete, Subject.Algo],
    //admin gets no EdfExport: no RAW DATA and no Export EDF (DWA-1539)

    [Action.View, Subject.Device],
    [Action.View, Subject.DeviceStatus],
    [Action.Edit, Subject.DeviceStatus],

    [Action.View, Subject.DeviceShipment],
    [Action.Edit, Subject.DeviceShipment],
    [Action.Create, Subject.DeviceShipment],
    // Naming a device and moving it between departments — inventory upkeep,
    // separate from editing what the device is.
    [Action.Edit, Subject.DeviceInventory],
    // Deactivating a device (taking it out of the active inventory) and
    // reactivating it. Same four roles as the inventory upkeep above.
    [Action.Edit, Subject.DeviceActivation],

    [Action.View, Subject.Department],
    [Action.Edit, Subject.Department],
    [Action.Create, Subject.Department],
    [Action.Delete, Subject.Department],


    [Action.View, Subject.Annotation],
    [Action.Edit, Subject.Annotation],
    [Action.Create, Subject.Annotation],
    [Action.Delete, Subject.Annotation],

    [Action.View, Subject.Analysis],
    [Action.Edit, Subject.Analysis],
    [Action.Create, Subject.Analysis],
    [Action.Delete, Subject.Analysis],

    [Action.View, Subject.Report],
    [Action.Edit, Subject.Report],
    [Action.Create, Subject.Report],
    //admin may edit report texts but not save them as custom texts: no CustomText

    [Action.View, Subject.ReferringDoctors],
    [Action.Edit, Subject.ReferringDoctors],
    [Action.Create, Subject.ReferringDoctors],
    [Action.Delete, Subject.ReferringDoctors],

    [Action.View, Subject.TestRoom],

    [Action.View, Subject.User],
    [Action.Edit, Subject.User],
    [Action.Create, Subject.User],
    [Action.Delete, Subject.User],
  ],

  [RolesEnum.Scorer]: [
    [Action.View, Subject.TechNote],
    [Action.Edit, Subject.TechNote],
    [Action.View, Subject.ScorerNote],
    [Action.Edit, Subject.ScorerNote],
    [Action.View, Subject.DashboardPage],
    [Action.View, Subject.PatientsPage],
    [Action.View, Subject.TestsPage],

    [Action.View, Subject.TestsLogs],
    [Action.View, Subject.TestsReport],
    [Action.View, Subject.LiveTestsPage],

    [Action.View, Subject.DevicesPage],
    [Action.View, Subject.TestRoomPage],
    [Action.View, Subject.SettingsPage],
    [Action.View, Subject.ReportsPage],
    [Action.View, Subject.WorkingAreaPage],
    [Action.View, Subject.SystemLogsPage],

    [Action.View, Subject.Patient],
    [Action.Edit, Subject.Patient],
    [Action.Create, Subject.Patient],
    [Action.Delete, Subject.Patient],

    [Action.View, Subject.Order],
    [Action.Edit, Subject.Order],
    [Action.Create, Subject.Order],
    [Action.Delete, Subject.Order],

    [Action.View, Subject.TestRecord],
    [Action.Edit, Subject.TestRecord],
    [Action.Create, Subject.TestRecord],
    [Action.Delete, Subject.TestRecord],

    [Action.View, Subject.Algo],
    [Action.Edit, Subject.Algo],
    [Action.Create, Subject.Algo],
    [Action.Delete, Subject.Algo],
    [Action.Create, Subject.EdfExport],

    [Action.View, Subject.Report],
    [Action.Edit, Subject.Report],
    [Action.Create, Subject.Report],
    [Action.Delete, Subject.Report],

    [Action.Edit, Subject.CustomText],
    [Action.Create, Subject.CustomText],
    [Action.Delete, Subject.CustomText],

    [Action.View, Subject.Device],
    [Action.View, Subject.DeviceStatus],
    [Action.Edit, Subject.DeviceStatus],

    [Action.View, Subject.DeviceShipment],
    [Action.Edit, Subject.DeviceShipment],
    [Action.Create, Subject.DeviceShipment],

    [Action.View, Subject.TestRoom],

    [Action.View, Subject.Department],

    [Action.View, Subject.Annotation],
    [Action.Edit, Subject.Annotation],
    [Action.Create, Subject.Annotation],
    [Action.Delete, Subject.Annotation],

    [Action.View, Subject.Analysis],
    [Action.Edit, Subject.Analysis],
    [Action.Create, Subject.Analysis],
    [Action.Delete, Subject.Analysis],

    [Action.View, Subject.ReferringDoctors],
    [Action.Edit, Subject.ReferringDoctors],
    [Action.Create, Subject.ReferringDoctors],
    [Action.Delete, Subject.ReferringDoctors],
  ],

  [RolesEnum.Technician]: [
    [Action.View, Subject.TechNote],
    [Action.Edit, Subject.TechNote],
    [Action.View, Subject.ScorerNote],
    [Action.View, Subject.DashboardPage],
    [Action.View, Subject.PatientsPage],
    [Action.View, Subject.TestsPage],

    [Action.View, Subject.TestsLogs],
    [Action.View, Subject.TestsReport],
    [Action.View, Subject.LiveTestsPage],

    [Action.View, Subject.DevicesPage],
    [Action.View, Subject.TestRoomPage],
    [Action.View, Subject.SettingsPage],
    [Action.View, Subject.ReportsPage],
    [Action.View, Subject.WorkingAreaPage],
    [Action.View, Subject.SystemLogsPage],

    [Action.View, Subject.Patient],
    [Action.Edit, Subject.Patient],
    [Action.Create, Subject.Patient],
    [Action.Delete, Subject.Patient],

    [Action.View, Subject.Order],
    [Action.Edit, Subject.Order],
    [Action.Create, Subject.Order],
    [Action.Delete, Subject.Order],

    [Action.View, Subject.TestRecord],
    [Action.Edit, Subject.TestRecord],
    [Action.Create, Subject.TestRecord],
    [Action.Delete, Subject.TestRecord],

    [Action.View, Subject.Algo],
    [Action.Edit, Subject.Algo],
    [Action.Create, Subject.Algo],
    [Action.Delete, Subject.Algo],
    [Action.Create, Subject.EdfExport],

    [Action.View, Subject.Report],
    [Action.Edit, Subject.Report],
    [Action.Create, Subject.Report],
    [Action.Delete, Subject.Report],

    [Action.Edit, Subject.CustomText],
    [Action.Create, Subject.CustomText],
    [Action.Delete, Subject.CustomText],

    [Action.View, Subject.Device],
    [Action.View, Subject.DeviceStatus],
    [Action.Edit, Subject.DeviceStatus],

    [Action.View, Subject.DeviceShipment],
    [Action.Edit, Subject.DeviceShipment],
    [Action.Create, Subject.DeviceShipment],

    [Action.View, Subject.Department],

    [Action.View, Subject.Annotation],
    [Action.Edit, Subject.Annotation],
    [Action.Create, Subject.Annotation],
    [Action.Delete, Subject.Annotation],

    [Action.View, Subject.Analysis],
    [Action.Edit, Subject.Analysis],
    [Action.Create, Subject.Analysis],
    [Action.Delete, Subject.Analysis],

    [Action.View, Subject.ReferringDoctors],
    [Action.Edit, Subject.ReferringDoctors],
    [Action.Create, Subject.ReferringDoctors],
    [Action.Delete, Subject.ReferringDoctors],

    [Action.View, Subject.TestRoom],

    [Action.View, Subject.User],
  ],

  [RolesEnum.Physician]: [
    [Action.View, Subject.TechNote],
    [Action.View, Subject.ScorerNote],
    [Action.Generate, Subject.Report],

    [Action.View, Subject.DashboardPage],
    [Action.View, Subject.PatientsPage],
    [Action.View, Subject.TestsPage],

    [Action.View, Subject.TestsLogs],
    [Action.View, Subject.TestsReport],
    [Action.View, Subject.LiveTestsPage],

    [Action.View, Subject.DevicesPage],
    [Action.View, Subject.TestRoomPage],
    [Action.View, Subject.SettingsPage],
    [Action.View, Subject.ReportsPage],
    [Action.View, Subject.WorkingAreaPage],
    [Action.View, Subject.SystemLogsPage],

    [Action.View, Subject.Patient],
    [Action.Edit, Subject.Patient],
    [Action.Create, Subject.Patient],
    [Action.Delete, Subject.Patient],

    [Action.View, Subject.Order],
    [Action.Edit, Subject.Order],
    [Action.Create, Subject.Order],
    [Action.Delete, Subject.Order],

    [Action.View, Subject.TestRecord],
    [Action.Edit, Subject.TestRecord],
    [Action.Create, Subject.TestRecord],
    [Action.Delete, Subject.TestRecord],

    [Action.View, Subject.Algo],
    [Action.Edit, Subject.Algo],
    [Action.Create, Subject.Algo],
    [Action.Delete, Subject.Algo],
    [Action.Create, Subject.EdfExport],

    [Action.View, Subject.Device],
    [Action.View, Subject.DeviceStatus],
    [Action.Edit, Subject.DeviceStatus],

    [Action.View, Subject.DeviceShipment],
    [Action.Edit, Subject.DeviceShipment],
    [Action.Create, Subject.DeviceShipment],

    [Action.View, Subject.Department],

    [Action.View, Subject.Report],
    [Action.Edit, Subject.Report],
    [Action.Create, Subject.Report],
    [Action.Delete, Subject.Report],

    [Action.Create, Subject.SignedReport],

    [Action.Edit, Subject.CustomText],
    [Action.Create, Subject.CustomText],
    [Action.Delete, Subject.CustomText],

    [Action.View, Subject.Annotation],
    [Action.Edit, Subject.Annotation],
    [Action.Create, Subject.Annotation],
    [Action.Delete, Subject.Annotation],

    [Action.View, Subject.Analysis],
    [Action.Edit, Subject.Analysis],
    [Action.Create, Subject.Analysis],
    [Action.Delete, Subject.Analysis],

    [Action.View, Subject.ReferringDoctors],
    [Action.Edit, Subject.ReferringDoctors],
    [Action.Create, Subject.ReferringDoctors],
    [Action.Delete, Subject.ReferringDoctors],

    [Action.View, Subject.TestRoom],

    [Action.View, Subject.User],
  ],

  [RolesEnum.OutsourceScorer]: [
    [Action.View, Subject.TechNote],
    [Action.Edit, Subject.TechNote],
    [Action.View, Subject.ScorerNote],
    [Action.Edit, Subject.ScorerNote],
    [Action.View, Subject.TestsPage],
    [Action.View, Subject.WorkingAreaPage],
    [Action.View, Subject.Patient],

    [Action.View, Subject.TestRecord],

    [Action.View, Subject.Algo],
    [Action.Edit, Subject.Algo],
    [Action.Create, Subject.Algo],
    // Kept so the EDF buttons stay exactly as they were before EdfExport was
    // split out of Algo. NOTE: the API's /tests/:id/export-edf allowlist has no
    // OUTSOURCE_SCORER, so these still 403 — a pre-existing bug, not this split.
    [Action.Create, Subject.EdfExport],

    [Action.View, Subject.Department],

    [Action.View, Subject.Annotation],
    [Action.Edit, Subject.Annotation],
    [Action.Create, Subject.Annotation],
    [Action.Delete, Subject.Annotation],

    [Action.View, Subject.Analysis],
    [Action.Edit, Subject.Analysis],
    [Action.Create, Subject.Analysis],
    [Action.Delete, Subject.Analysis],
  ],

  [RolesEnum.LabManager]: [
    [Action.View, Subject.TechNote],
    [Action.Edit, Subject.TechNote],
    [Action.View, Subject.ScorerNote],
    [Action.Edit, Subject.ScorerNote],
    [Action.Generate, Subject.Report],
    [Action.View, Subject.DashboardPage],
    [Action.View, Subject.PatientsPage],
    [Action.View, Subject.TestsPage],

    [Action.View, Subject.TestsLogs],
    [Action.View, Subject.TestsReport],
    [Action.View, Subject.LiveTestsPage],

    [Action.View, Subject.DevicesPage],
    [Action.View, Subject.TestRoomPage],
    [Action.View, Subject.SettingsPage],
    [Action.View, Subject.ReportsPage],
    [Action.View, Subject.WorkingAreaPage],
    [Action.View, Subject.SystemLogsPage],

    [Action.View, Subject.Patient],
    [Action.Edit, Subject.Patient],
    [Action.Create, Subject.Patient],
    [Action.Delete, Subject.Patient],

    [Action.View, Subject.Order],
    [Action.Edit, Subject.Order],
    [Action.Create, Subject.Order],
    [Action.Delete, Subject.Order],

    [Action.View, Subject.TestRecord],
    [Action.Edit, Subject.TestRecord],
    [Action.Create, Subject.TestRecord],
    [Action.Delete, Subject.TestRecord],
    [Action.Edit, Subject.TestBackupFile],

    [Action.View, Subject.Algo],
    [Action.Edit, Subject.Algo],
    [Action.Create, Subject.Algo],
    [Action.Delete, Subject.Algo],
    [Action.Create, Subject.EdfExport],

    [Action.View, Subject.Report],
    [Action.Edit, Subject.Report],
    [Action.Create, Subject.Report],
    [Action.Delete, Subject.Report],

    [Action.Edit, Subject.CustomText],
    [Action.Create, Subject.CustomText],
    [Action.Delete, Subject.CustomText],

    [Action.View, Subject.Device],
    [Action.View, Subject.DeviceStatus],
    [Action.Edit, Subject.DeviceStatus],

    [Action.View, Subject.DeviceShipment],
    [Action.Edit, Subject.DeviceShipment],
    [Action.Create, Subject.DeviceShipment],
    // Naming a device and moving it between departments — inventory upkeep,
    // separate from editing what the device is.
    [Action.Edit, Subject.DeviceInventory],
    // Deactivating a device (taking it out of the active inventory) and
    // reactivating it. Same four roles as the inventory upkeep above.
    [Action.Edit, Subject.DeviceActivation],

    [Action.View, Subject.Department],
    [Action.Edit, Subject.Department],

    [Action.View, Subject.Annotation],
    [Action.Edit, Subject.Annotation],
    [Action.Create, Subject.Annotation],
    [Action.Delete, Subject.Annotation],

    [Action.View, Subject.Analysis],
    [Action.Edit, Subject.Analysis],
    [Action.Create, Subject.Analysis],
    [Action.Delete, Subject.Analysis],

    [Action.View, Subject.ReferringDoctors],
    [Action.Edit, Subject.ReferringDoctors],
    [Action.Create, Subject.ReferringDoctors],
    [Action.Delete, Subject.ReferringDoctors],

    [Action.View, Subject.TestRoom],
    [Action.Edit, Subject.TestRoom],
    [Action.Create, Subject.TestRoom],
    [Action.Delete, Subject.TestRoom],

    [Action.View, Subject.User],
  ],

  [RolesEnum.Viewer]: [
    [Action.View, Subject.TechNote],
    [Action.View, Subject.ScorerNote],
    [Action.View, Subject.DashboardPage],
    [Action.View, Subject.DashboardPage],
    [Action.View, Subject.PatientsPage],
    [Action.View, Subject.TestsPage],

    [Action.View, Subject.TestsLogs],
    [Action.View, Subject.TestsReport],
    [Action.View, Subject.LiveTestsPage],

    [Action.View, Subject.DevicesPage],
    [Action.View, Subject.TestRoomPage],
    [Action.View, Subject.SettingsPage],
    [Action.View, Subject.ReportsPage],
    [Action.View, Subject.WorkingAreaPage],
    [Action.View, Subject.SystemLogsPage],

    [Action.View, Subject.All],
  ],
  [RolesEnum.TechAdmin]: [
    [Action.View, Subject.TechNote],
    [Action.Edit, Subject.TechNote],
    [Action.View, Subject.ScorerNote],
    [Action.Edit, Subject.ScorerNote],
    [Action.Generate, Subject.Report],

    [Action.View, Subject.DashboardPage],
    [Action.View, Subject.TestRoomPage],
    [Action.View, Subject.PatientsPage],
    [Action.View, Subject.TestsPage],

    [Action.View, Subject.TestsLogs],
    [Action.View, Subject.TestsReport],
    [Action.View, Subject.LiveTestsPage],

    [Action.View, Subject.DevicesPage],
    [Action.View, Subject.SettingsPage],
    [Action.View, Subject.ReportsPage],
    [Action.View, Subject.WorkingAreaPage],
    [Action.View, Subject.SystemLogsPage],

    [Action.View, Subject.Patient],
    [Action.Edit, Subject.Patient],
    [Action.Create, Subject.Patient],
    [Action.Delete, Subject.Patient],

    [Action.View, Subject.Order],
    [Action.Edit, Subject.Order],
    [Action.Create, Subject.Order],
    [Action.Delete, Subject.Order],

    [Action.View, Subject.TestRecord],
    [Action.Edit, Subject.TestRecord],
    [Action.Create, Subject.TestRecord],
    [Action.Delete, Subject.TestRecord],
    [Action.Edit, Subject.TestBackupFile],

    [Action.View, Subject.Algo],
    [Action.Edit, Subject.Algo],
    [Action.Create, Subject.Algo],
    [Action.Delete, Subject.Algo],
    [Action.Create, Subject.EdfExport],

    [Action.View, Subject.Device],
    [Action.Edit, Subject.Device],
    [Action.Create, Subject.Device],
    [Action.Delete, Subject.Device],

    [Action.View, Subject.DeviceStatus],
    [Action.Edit, Subject.DeviceStatus],

    [Action.View, Subject.DeviceShipment],
    [Action.Edit, Subject.DeviceShipment],
    [Action.Create, Subject.DeviceShipment],
    // Naming a device and moving it between departments — inventory upkeep,
    // separate from editing what the device is.
    [Action.Edit, Subject.DeviceInventory],
    // Deactivating a device (taking it out of the active inventory) and
    // reactivating it. Same four roles as the inventory upkeep above.
    [Action.Edit, Subject.DeviceActivation],

    [Action.View, Subject.Department],
    [Action.Edit, Subject.Department],
    [Action.Create, Subject.Department],
    [Action.Delete, Subject.Department],

    [Action.View, Subject.Annotation],
    [Action.Edit, Subject.Annotation],
    [Action.Create, Subject.Annotation],
    [Action.Delete, Subject.Annotation],

    [Action.View, Subject.Analysis],
    [Action.Edit, Subject.Analysis],
    [Action.Create, Subject.Analysis],
    [Action.Delete, Subject.Analysis],

    [Action.View, Subject.Report],
    [Action.Edit, Subject.Report],
    [Action.Create, Subject.Report],
    [Action.Delete, Subject.Report],

    [Action.Create, Subject.SignedReport],

    [Action.Edit, Subject.CustomText],
    [Action.Create, Subject.CustomText],
    [Action.Delete, Subject.CustomText],

    [Action.View, Subject.ReferringDoctors],
    [Action.Edit, Subject.ReferringDoctors],
    [Action.Create, Subject.ReferringDoctors],
    [Action.Delete, Subject.ReferringDoctors],

    [Action.View, Subject.TestRoom],
    [Action.Edit, Subject.TestRoom],
    [Action.Create, Subject.TestRoom],
    [Action.Delete, Subject.TestRoom],

    [Action.View, Subject.User],
    [Action.Edit, Subject.User],
    [Action.Create, Subject.User],
    [Action.Delete, Subject.User],

    [Action.View, Subject.PSGHSTestsPage],
    [Action.View, Subject.RAWTestsPage],
  ],

  [RolesEnum.SharedPhysician]: [
    [Action.View, Subject.TechNote],
    [Action.View, Subject.ScorerNote],
    [Action.View, Subject.PhysicianDashboardPage],
    [Action.View, Subject.WorkingAreaPage],
    [Action.View, Subject.ReportsPage],

    [Action.Generate, Subject.Report],
    [Action.Create, Subject.Report],
    [Action.Edit, Subject.Report],

    [Action.Create, Subject.SignedReport],

    [Action.Edit, Subject.CustomText],
    [Action.Create, Subject.CustomText],
    [Action.Delete, Subject.CustomText],

    [Action.View, Subject.Algo],
    [Action.Edit, Subject.Algo],
    [Action.Create, Subject.Algo],
    [Action.Delete, Subject.Algo],
    [Action.Create, Subject.EdfExport],

    [Action.View, Subject.TestRecord],
    [Action.Edit, Subject.TestRecord],
    [Action.Create, Subject.TestRecord],

    [Action.View, Subject.Annotation],
    [Action.Edit, Subject.Annotation],
    [Action.Create, Subject.Annotation],
    [Action.Delete, Subject.Annotation],

    [Action.View, Subject.Analysis],
    [Action.Edit, Subject.Analysis],
    [Action.Create, Subject.Analysis],
    [Action.Delete, Subject.Analysis],

    [Action.View, Subject.TestsReport],
  ],
};

export const ROLE_PERMISSIONS: Record<RolesEnum, AbilityTuple[]> = {
  ...BASE_ROLE_PERMISSIONS,
  /**
   * The non-signing Physician. Derived from the Interpreting Physician rather
   * than written out, so the two can only ever differ by the one permission
   * that defines the role — a new physician permission reaches both without
   * anyone having to remember this entry.
   */
  [RolesEnum.PhysicianNoSign]: BASE_ROLE_PERMISSIONS[
    RolesEnum.Physician
  ].filter(
    ([action, subject]) =>
      !(action === Action.Create && subject === Subject.SignedReport),
  ),
};
